import { PixelRatio, Platform, Pressable, StyleSheet, View } from "react-native";
import React, {
    useCallback,
    useState,
    useMemo,
    useEffect,
    useImperativeHandle,
    forwardRef,
} from "react";
import { DateTimePickerProps } from "react-native-modal-datetime-picker";
import { DatePicker } from "./DatePicker";
import BaseField, { BaseFieldProps } from "./BaseField";
import { useTheme } from "../hooks/use-theme";
import Text from "./Text";
import TimeField from "./TimeField";
import { minutesAsTimeString } from "../utils";
import * as Device from "expo-device";
import { useDateFormatter } from "../hooks/date";
import { PressableWithFocus } from "./PressableWithFocus";

const FONT_SCALE = PixelRatio.getFontScale();

const BASE_TIME_TEXT_LINE_HEIGHT = 28;
const SCALED_TIME_TEXT_LINE_HEIGHT =
    BASE_TIME_TEXT_LINE_HEIGHT - (FONT_SCALE - 1) * (BASE_TIME_TEXT_LINE_HEIGHT / 1.5);

const BASE_TIME_CONTAINER_MIN_WIDTH = 62;
const SCALED_TIME_CONTAINER_MIN_WIDTH = BASE_TIME_CONTAINER_MIN_WIDTH * FONT_SCALE;

type IOSDisplay = "default" | "compact" | "inline" | "spinner";

type DateFieldProps = Omit<BaseFieldProps, "component" | "hasValue" | "value" | "disabled"> &
    Omit<DateTimePickerProps, "onConfirm" | "onCancel" | "date"> & {
        label?: string;
        minimumDate?: Date;
        maximumDate?: Date;
        setDateFn?: string;
        display?: IOSDisplay;
        disabled?: boolean;
        date?: Date | null;
        value?: Date | null;
        onChange?: (date: Date | null) => void;
        timePicker?: boolean | `${number}:${number}`; // Can either gives true or a default time (ex. 07:30). If time is not defined, the default will be 0:00
    };

type DateFieldHandle = {
    focus: () => void;
};

const buildZuluDate = (baseDate: Date, h: number = 0, m: number = 0, s: number = 0): Date => {
    const year = baseDate.getFullYear();
    const month = `${baseDate.getMonth() + 1}`.padStart(2, "0");
    const day = `${baseDate.getDate()}`.padStart(2, "0");
    const hours = `${h}`.padStart(2, "0");
    const minutes = `${m}`.padStart(2, "0");
    const seconds = `${s}`.padStart(2, "0");

    // new date is now in UTC
    return new Date(`${year}-${month}-${day}T${hours}:${minutes}:${seconds}.000Z`);
};

// eslint-disable-next-line react/display-name
const DateField = forwardRef<DateFieldHandle, DateFieldProps>(
    (
        {
            value,
            onChange,
            label,
            placeholder,
            date,
            isSelfContained,
            minimumDate,
            maximumDate,
            style,
            error,
            left,
            right = !left ? "calendar" : undefined,
            disabled,
            setDateFn,
            display = "inline",
            testID,
            timePicker = false,
            ...props
        },
        ref,
    ) => {
        const [showDateChooser, setShowDateChooser] = useState(false);
        const [timeTmp, setTimeTmp] = useState<number | undefined>(
            typeof timePicker === "string"
                ? timePicker
                      .split(":")
                      .map((n) => parseInt(n))
                      .reduce((result, t, index) => result + t * (index === 0 ? 60 : 1), 0)
                : undefined,
        );

        const handleNewDate = useCallback(
            (inputDate: Date | null) => {
                if (inputDate) {
                    // extract only the information we need
                    let newDate = buildZuluDate(inputDate);
                    if (timeTmp) {
                        const [h, m] = minutesAsTimeString(timeTmp)
                            .split(":")
                            .map((n) => parseInt(n));
                        newDate = buildZuluDate(inputDate, h, m, 0);
                    }

                    setShowDateChooser(false);

                    if (minimumDate && newDate < minimumDate) {
                        // DateTimePickerModal will return today's date if user just click confirm.
                        // So we need to check if the date is not before the minimum date
                        onChange?.(minimumDate);
                        return;
                    }
                    onChange?.(newDate);
                } else {
                    onChange?.(null);
                }
            },
            [minimumDate, onChange, timeTmp],
        );

        const handleNewTime = useCallback(
            (time: number) => {
                setTimeTmp(time);

                if (value) {
                    const [h, m] = minutesAsTimeString(time)
                        .split(":")
                        .map((n) => parseInt(n));
                    const newDate = buildZuluDate(value, h, m, 0);
                    onChange?.(newDate);
                }
            },
            [onChange, value],
        );

        useEffect(() => {
            if (Platform.OS === "web" && setDateFn) {
                const fnName = `set${setDateFn}`;
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                window[fnName] = (date: string) => {
                    onChange?.(new Date(date));
                };

                return () => {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    window[fnName] = null;
                };
            }
        }, [onChange, setDateFn]);

        const formatDate = useDateFormatter();

        const focus = useCallback(() => {
            setShowDateChooser(true);
        }, []);

        useImperativeHandle(ref, () => ({ focus }));

        /*
            Depending on the app/device, the DatePicker could have a "managed" behavior or not, so we either directly display it or control it with 'isVisible' prop or showPicker() on web.

            Native app: Always show the <Pressable /> component
            Web app: Show the <Pressable /> component only if not iOS, iPadOS, or Android devices
        */
        const hasPressable = useMemo(
            () =>
                Platform.OS !== "web" ||
                !["iOS", "iPadOS", "Android"].includes(Device.osName || ""),
            [],
        );

        const toggle = useCallback(() => {
            setShowDateChooser(!showDateChooser);
        }, [showDateChooser]);

        const [isTimeFocused, setIsTimeFocused] = useState(false);

        return (
            <>
                <BaseField
                    component={
                        <PressableWithFocus onPress={toggle} disabled={disabled}>
                            {timePicker && (
                                <TimePicker
                                    value={timeTmp || 0}
                                    onChange={handleNewTime}
                                    disabled={disabled}
                                    onFocus={() => {
                                        setIsTimeFocused(true);
                                    }}
                                    onBlur={() => {
                                        setIsTimeFocused(false);
                                    }}
                                />
                            )}

                            {hasPressable && (
                                <Pressable
                                    id={testID}
                                    style={{
                                        position: "absolute",
                                        height: "100%",
                                        width: "100%",
                                        zIndex: 100,
                                    }}
                                    onPress={toggle}
                                    disabled={disabled}
                                />
                            )}
                            <DatePicker
                                date={value || date || undefined}
                                display={display}
                                isVisible={showDateChooser}
                                onConfirm={handleNewDate}
                                onCancel={() => {
                                    setShowDateChooser(false);
                                }}
                                minimumDate={minimumDate}
                                maximumDate={maximumDate}
                                // ensure timezone is not altering date value
                                {...props}
                            />
                        </PressableWithFocus>
                    }
                    value={
                        value
                            ? formatDate(value, {
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                              })
                            : ""
                    }
                    isFocused={showDateChooser}
                    label={label}
                    placeholder={placeholder}
                    left={left}
                    right={right}
                    error={error}
                    testID={testID}
                    disabled={disabled}
                    style={[style, isTimeFocused && { zIndex: 1 }]}
                    isSelfContained={isSelfContained}
                    asButton
                />
            </>
        );
    },
);

type TimePickerProps = {
    value: number;
    onChange: (time: number) => void;
    onFocus: () => void;
    onBlur: () => void;
    disabled?: boolean;
};

const TimePicker = ({ value, disabled, ...props }: TimePickerProps) => {
    const styles = useStyles();
    const [hours, minutes] = useMemo(() => minutesAsTimeString(value).split(":"), [value]);

    return (
        <View style={[styles.container, disabled && styles.disabled]}>
            <Text style={styles.text}>
                {hours.padStart(2, "0")}:{minutes.padStart(2, "0")}
            </Text>
            <TimeField
                value={value}
                disabled={disabled}
                style={styles.timeField}
                popupFallback
                {...props}
            />
        </View>
    );
};

const useStyles = () => {
    const { fonts, colors } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                container: {
                    position: "absolute",
                    zIndex: 1000,
                    right: 0,
                    backgroundColor: colors.body,
                    height: 28,
                    paddingHorizontal: 7,
                    minWidth: SCALED_TIME_CONTAINER_MIN_WIDTH,
                    borderRadius: 14,
                    alignItems: "center",
                    justifyContent: "center",
                },
                disabled: {
                    backgroundColor: colors.lighterGrey,
                },
                text: {
                    ...fonts.bodyLarge,
                    fontSize: 14,
                    lineHeight: SCALED_TIME_TEXT_LINE_HEIGHT,
                    alignItems: "center",
                    justifyContent: "center",
                    textAlignVertical: "center",
                    letterSpacing: 1.4,
                    color: colors.background,
                },
                timeField: {
                    position: "absolute",
                    paddingHorizontal: 0,
                    height: "100%",
                    width: "100%",
                    opacity: 0,
                },
            }),
        [colors.background, colors.body, colors.lighterGrey, fonts.bodyLarge],
    );
};

export default DateField;
